// Shared hook over GET /v1/boats. The redesign needs the boat list in several
// places — the single-boat redirect, the boat name + switcher in the app nav,
// and the multi-boat switcher landing — so the three pages that used to each
// refetch it now share one hook. Reuses the api() client + the 401 ->
// auth.refresh() pattern the rest of the surface uses.

import type { SimTimeStatus } from './playback';
import type { FirmwareUpdateAvailable } from '@neomaris/protocol';
import { useCallback, useEffect, useState } from 'react';

import { api } from '../api';
import { useAuth } from '../auth';

/** A boat's own photo (TOM-683): a new photo is a new version, so a new URL. */
export interface BoatPhoto {
  version: string;
  width: number;
  height: number;
  setAt: string;
}

export interface Boat {
  id: string;
  display_name: string;
  created_at: string;
  last_contacted_at: string | null;
  // Last-reported core version (hb runtime-identity stamp, migration 0049).
  // null = the boat has never reported one — render "version unknown".
  core_version: string | null;
  // The newer signed release this boat can actually install. Additive for
  // compatibility with older clouds; absent/null means no actionable update.
  coreUpdateAvailable?: string | null;
  // Playback (sim-time) clock the boat reported (migration 0088). Null on a
  // wall-clock boat. The pair is linear - see lib/playback.ts.
  simTime?: SimTimeStatus | null;
  // Mandatory-update enforcement state (core-update-mechanism phase 4):
  // 'paused' = cloud sync stopped pending the update (distinct from offline),
  // 'follow_up' = subject to enforcement but exempt (unknown/old version),
  // 'ok' = nothing to surface.
  enforcementState: 'paused' | 'follow_up' | 'ok';
  // The boat's own photo for its home header, or null (TOM-683, ADR 0397):
  // fetch /v1/boats/:id/photo?v=<version>. Absent from an older cloud.
  photo?: BoatPhoto | null;
  // What the boat said the LAST core-update approval did (ADR 0178). null =
  // no approval has ever reported. `core_version` cannot stand in for this: a
  // failed update leaves the version unchanged, which looks exactly like one
  // still installing. `commandKey` is the `idempotencyKey` the install route
  // returned — compare it against the key you hold before treating the outcome
  // as your own attempt's, or a stale failure renders as a fresh one.
  lastUpdate: {
    commandKey: string | null;
    phase: 'accepted' | 'rejected' | 'complete' | 'failed';
    version: string | null;
    detail: string | null;
    at: string | null;
  } | null;
  // NeOS gateway firmware block (ADR 0202; core ADR-164). null (or absent,
  // from an older cloud) = not a firmware-managed boat — render no firmware
  // chrome at all. `updateAvailable` is the complete verified consent fact
  // for this gateway's reported RAUC compatible string; `lastUpdate` follows
  // the exact ADR 0178 attempt-matching contract, correlated by the firmware
  // install route's `idempotencyKey`.
  firmware?: {
    updater: string;
    version: string | null;
    updateAvailable: FirmwareUpdateAvailable | null;
    lastUpdate: {
      commandKey: string | null;
      phase: 'accepted' | 'rejected' | 'complete' | 'failed';
      version: string | null;
      detail: string | null;
      at: string | null;
    } | null;
  } | null;
}

export type BoatsState =
  | { kind: 'loading' }
  | { kind: 'ready'; boats: Boat[] }
  | { kind: 'error'; message: string };

export interface UseBoats {
  state: BoatsState;
  refresh: () => void;
}

export function useBoats(): UseBoats {
  const auth = useAuth();
  const [state, setState] = useState<BoatsState>({ kind: 'loading' });
  const [tick, setTick] = useState(0);
  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await api<{ boats: Boat[] }>('/v1/boats');
        if (!cancelled) setState({ kind: 'ready', boats: data.boats });
      } catch (err) {
        const e = err as { status?: number; message?: string };
        if (e.status === 401) {
          await auth.refresh();
          return;
        }
        if (!cancelled) setState({ kind: 'error', message: e.message ?? 'Failed to load boats.' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth, tick]);

  return { state, refresh };
}
